import type { StoryblokRichtext } from "~/types/storyblok";

/**
 * Determines whether a given rich text object contains any meaningful content.
 *
 * This function recursively traverses the nodes of a rich text structure (such as those
 * returned by Storyblok) to check if there is meaningful content including:
 * - Text nodes with non-whitespace content
 * - Images, videos, and other media
 * - Embeds and blok components
 * - Other rich content types
 *
 * @param richText - The rich text object to check, expected to have a `content` array.
 * @returns `true` if there is meaningful content; otherwise, `false`.
 */
export default function hasRichTextContent(richText: StoryblokRichtext | null | undefined) {
  if (!richText || !richText.content || !Array.isArray(richText.content) || richText.content.length === 0) {
    return false;
  }

  // Inner recursive function to check nodes for meaningful content
  const checkNodesForContent = (nodes: StoryblokRichtext[]) => {
    if (!nodes || !Array.isArray(nodes)) {
      return false;
    }
    for (const node of nodes) {
      // Check for text in a 'text' node
      if (node.type === "text" && node.text && node.text.trim() !== "") {
        return true; // Found actual text
      }

      // Check for media content (images, videos, etc.)
      if (node.type === "image" || node.type === "video" || node.type === "audio") {
        return true; // Found media content
      }

      // Check for embeds and other rich content
      if (node.type === "embed" || node.type === "iframe") {
        return true; // Found embed content
      }

      // Check for blok components
      if (node.type === "blok" && node.attrs && node.attrs.body.length > 0) {
        return true; // Found blok content
      }

      // Check for horizontal rules, breaks, and other self-contained content
      if (node.type === "horizontal_rule" || node.type === "hard_break") {
        return true; // Found structural content
      }

      // Recursively check 'content' of container node types (e.g., paragraph, heading, list_item)
      if (node.content && checkNodesForContent(node.content)) {
        return true; // Found content in a nested node
      }
    }
    return false; // No meaningful content found in this array of nodes
  };

  return checkNodesForContent(richText.content);
}
